/**
 * AudienceScopeField — distribution-scope picker for Announcements + Events.
 * "All staff" or "Specific departments" + a department multi-select. Workers
 * only see items scoped to 'all' or to a department they belong to (the
 * worker dashboard feed filters server-side). Shared by both create dialogs.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Checkbox, FormControl, InputLabel, ListItemText, MenuItem,
  OutlinedInput, Select, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { organizationApi } from '../../api';

interface DeptLite { id: string; name: string }

export interface AudienceScopeValue {
  audience_scope?: string;
  audience_department_ids?: string[];
}

interface Props {
  scope: string;
  departmentIds: string[];
  onChange: (next: AudienceScopeValue) => void;
}

const AudienceScopeField: React.FC<Props> = ({ scope, departmentIds, onChange }) => {
  const { t } = useTranslation();
  const [depts, setDepts] = useState<DeptLite[]>([]);

  useEffect(() => {
    let alive = true;
    organizationApi.departmentsApi
      .list()
      .then((rows) => { if (alive) setDepts((rows as DeptLite[]) ?? []); })
      .catch(() => { if (alive) setDepts([]); });
    return () => { alive = false; };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <FormControl fullWidth size="small">
        <InputLabel>{t('org:audience.label')}</InputLabel>
        <Select
          label={t('org:audience.label')}
          value={scope || 'all'}
          onChange={(e) =>
            onChange({
              audience_scope: e.target.value,
              audience_department_ids: e.target.value === 'all' ? [] : departmentIds,
            })
          }
        >
          <MenuItem value="all">{t('org:audience.all')}</MenuItem>
          <MenuItem value="departments">{t('org:audience.departments')}</MenuItem>
        </Select>
      </FormControl>

      {scope === 'departments' && (
        <FormControl fullWidth size="small">
          <InputLabel>{t('org:audience.pickDepartments')}</InputLabel>
          <Select
            multiple
            label={t('org:audience.pickDepartments')}
            value={departmentIds}
            input={<OutlinedInput label={t('org:audience.pickDepartments')} />}
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                audience_scope: 'departments',
                audience_department_ids: typeof v === 'string' ? v.split(',') : v,
              });
            }}
            renderValue={(selected) =>
              depts.filter((d) => (selected as string[]).includes(d.id)).map((d) => d.name).join(', ')
            }
          >
            {depts.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                <Checkbox checked={departmentIds.includes(d.id)} />
                <ListItemText primary={d.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {scope === 'departments' && departmentIds.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('org:audience.hint')}
        </Typography>
      )}
    </Box>
  );
};

export default AudienceScopeField;
